import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Typography, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Code, Transform, ContentCopy, Clear } from '@mui/icons-material';
import './JsonTransformer.css';
import { apiFetch, buildAuthHeaders } from '../auth/apiFetch';

interface JsonTransformerProps {
  onTransform?: (inputJson: string) => Promise<string>;
}

export const JsonTransformer: React.FC<JsonTransformerProps> = ({ onTransform }) => {
  const [inputJson, setInputJson] = useState('');
  const [outputJson, setOutputJson] = useState('');
  const [isTransforming, setIsTransforming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidJson, setIsValidJson] = useState(true);
  const [selectedExample, setSelectedExample] = useState<string>('template');

  const validateJson = (jsonString: string): boolean => {
    if (!jsonString.trim()) return true;
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const formatJson = (jsonString: string): string => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setInputJson(value);
    setIsValidJson(validateJson(value));
    setError(null);
  };

  const handleTransform = async () => {
    if (!inputJson.trim()) {
      setError('Please enter JSON to transform');
      return;
    }

    if (!isValidJson) {
      setError('Invalid JSON format');
      return;
    }

    setIsTransforming(true);
    setError(null);

    try {
      if (onTransform) {
        const result = await onTransform(inputJson);
        setOutputJson(formatJson(result));
      } else {
        // Call the transform API endpoint
        const response = await apiFetch('/transform', {
          method: 'POST',
          headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
          body: inputJson,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.status === 'success') {
          setOutputJson(formatJson(JSON.stringify(result.transformedPayload)));
        } else {
          throw new Error(result.message || 'Transformation failed');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transformation failed');
      setOutputJson('');
    } finally {
      setIsTransforming(false);
    }
  };

  const handleCopyOutput = () => {
    navigator.clipboard.writeText(outputJson);
  };

  const handleClear = () => {
    setInputJson('');
    setOutputJson('');
    setError(null);
    setIsValidJson(true);
  };

  const getExampleJson = (type: string) => {
    const examples: { [key: string]: any } = {
      template: {
        "payloadVersion": 0.1,
        "msgId": "0.0.0.0.0.wzrk_default.-0.-1.",
        "to": "999999999999",
        "callbackURL": "https://in1.cb.wzrkt.com/rcs/generic?account=79R-9KW-8Z7Z",
        "rcsContent": {
          "senderId": "999999999999",
          "content": {
            "templateId": "CARD_2",
            "type": "TEMPLATE",
            "parameters": [
              {
                "name": "Name",
                "value": "John Doe"
              },
              {
                "name": "Balance",
                "value": "1000"
              }
            ]
          },
          "customParam": [
            {
              "name": "param1",
              "value": "RCSGlobalConfigParam"
            }
          ]
        }
      },
      text: {
        "payloadVersion": 0.1,
        "msgId": "CT_TXT_20240315_001_ABC123",
        "to": "+911234567890",
        "callbackURL": "https://your-callback-endpoint.com/rcs/callback",
        "rcsContent": {
          "senderId": "BRAND_RCS_001",
          "customParam": [
            {
              "name": "campaign_id",
              "value": "welcome_flow_2024"
            },
            {
              "name": "user_segment",
              "value": "premium_users"
            }
          ],
          "content": {
            "type": "TEXT",
            "text": "Welcome to our service! Would you like to explore our premium features?",
            "suggestions": [
              {
                "type": "REPLY",
                "text": "Yes, show me",
                "postbackData": "explore_premium"
              },
              {
                "type": "REPLY",
                "text": "Maybe later",
                "postbackData": "remind_later"
              },
              {
                "type": "OPEN_URL",
                "text": "Learn More",
                "postbackData": "visit_website",
                "url": "https://example.com/features"
              },
              {
                "type": "DIAL_PHONE",
                "text": "Call Expert",
                "postbackData": "expert_consultation",
                "phoneNumber": "+918001234567"
              }
            ]
          }
        }
      },
      card: {
        "payloadVersion": 0.1,
        "msgId": "CT_CARD_20240315_003_GHI789",
        "to": "+911234567890",
        "callbackURL": "https://your-callback-endpoint.com/rcs/callback",
        "rcsContent": {
          "senderId": "BRAND_RCS_001",
          "customParam": [
            {
              "name": "product_category",
              "value": "electronics"
            },
            {
              "name": "campaign_type",
              "value": "product_launch"
            }
          ],
          "content": {
            "type": "CARD",
            "orientation": "HORIZONTAL",
            "alignment": "LEFT",
            "cardContent": {
              "title": "Latest Smartphone Model XYZ",
              "description": "Experience cutting-edge technology with an advanced camera system, long-lasting battery, and premium design. Available in multiple colors.",
              "media": {
                "mediaUrl": "https://cdn.example.com/products/smartphone-xyz-hero.jpg",
                "thumbnailUrl": "https://cdn.example.com/products/smartphone-xyz-thumb.jpg",
                "height": "MEDIUM"
              },
              "suggestions": [
                {
                  "type": "OPEN_URL",
                  "text": "Buy Now - ₹49,999",
                  "postbackData": "purchase_smartphone_xyz",
                  "url": "https://store.example.com/smartphone-xyz"
                },
                {
                  "type": "DIAL_PHONE",
                  "text": "Call Expert",
                  "postbackData": "expert_consultation",
                  "phoneNumber": "+918001234567"
                },
                {
                  "type": "REPLY",
                  "text": "More Details",
                  "postbackData": "request_details_smartphone"
                }
              ]
            },
            "suggestions": [
              {
                "type": "REPLY",
                "text": "Know more",
                "postbackData": "user_query"
              }
            ]
          }
        }
      },
      carousel: {
        "payloadVersion": 0.1,
        "msgId": "CT_CAROUSEL_20240315_004_JKL012",
        "to": "+911234567890",
        "callbackURL": "https://your-callback-endpoint.com/rcs/callback",
        "rcsContent": {
          "senderId": "BRAND_RCS_001",
          "customParam": [
            {
              "name": "catalog_type",
              "value": "featured_products"
            },
            {
              "name": "season",
              "value": "spring_collection_2024"
            }
          ],
          "content": {
            "type": "CAROUSEL",
            "cardWidth": "MEDIUM",
            "cardContents": [
              {
                "title": "Laptop Pro Series",
                "description": "High-performance laptop for professionals with advanced graphics and all-day battery life.",
                "media": {
                  "mediaUrl": "https://cdn.example.com/products/laptop-pro-main.jpg",
                  "thumbnailUrl": "https://cdn.example.com/products/laptop-pro-thumb.jpg",
                  "height": "MEDIUM"
                },
                "suggestions": [
                  {
                    "type": "OPEN_URL",
                    "text": "From ₹89,999",
                    "postbackData": "laptop_pro_configure",
                    "url": "https://store.example.com/laptop-pro"
                  }
                ]
              },
              {
                "title": "Tablet Ultra",
                "description": "Versatile tablet with premium display, perfect for creativity and productivity tasks.",
                "media": {
                  "mediaUrl": "https://cdn.example.com/products/tablet-ultra-demo.mp4",
                  "thumbnailUrl": "https://cdn.example.com/products/tablet-ultra-thumb.jpg",
                  "height": "MEDIUM"
                },
                "suggestions": [
                  {
                    "type": "OPEN_URL",
                    "text": "From ₹54,999",
                    "postbackData": "tablet_ultra_purchase",
                    "url": "https://store.example.com/tablet-ultra"
                  }
                ]
              },
              {
                "title": "Smartwatch Elite",
                "description": "Advanced health monitoring, GPS, and smart features in a premium design.",
                "media": {
                  "mediaUrl": "https://cdn.example.com/products/smartwatch-elite.jpg",
                  "thumbnailUrl": "https://cdn.example.com/products/smartwatch-thumb.jpg",
                  "height": "MEDIUM"
                },
                "suggestions": [
                  {
                    "type": "OPEN_URL",
                    "text": "₹24,999",
                    "postbackData": "smartwatch_elite_shop",
                    "url": "https://store.example.com/smartwatch-elite"
                  }
                ]
              }
            ]
          }
        }
      }
    };
    return examples[type] || examples.template;
  };

  const handleExampleChange = (event: any) => {
    const exampleType = event.target.value;
    setSelectedExample(exampleType);
    const exampleJson = getExampleJson(exampleType);
    setInputJson(JSON.stringify(exampleJson, null, 2));
    setIsValidJson(true);
  };

  useEffect(() => {
    // Initialize with default template example
    const exampleJson = getExampleJson(selectedExample);
    setInputJson(JSON.stringify(exampleJson, null, 2));
  }, []);

  return (
    <div className="json-transformer">
      <div className="transformer-header">
        <Typography variant="h5" component="h2" className="transformer-title">
          <Code className="title-icon" />
          JSON RCS Payload Transformer
        </Typography>
        <Typography variant="body2" color="text.secondary" className="transformer-subtitle">
          Paste your JSON payload and transform it for troubleshooting
        </Typography>
      </div>

      <div className="transformer-content">
        <div className="input-section">
          <div className="section-header">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>CT RCS Payload Input</Typography>
            <div className="section-actions">
              <FormControl size="small" variant="outlined" sx={{ minWidth: 180, mr: 1 }}>
                <InputLabel>Example</InputLabel>
                <Select
                  value={selectedExample}
                  onChange={handleExampleChange}
                  label="Example"
                >
                  <MenuItem value="template">Template Example</MenuItem>
                  <MenuItem value="text">Text Example</MenuItem>
                  <MenuItem value="card">Card Example</MenuItem>
                  <MenuItem value="carousel">Carousel Example</MenuItem>
                </Select>
              </FormControl>
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={handleClear}
                variant="outlined"
                color="secondary"
              >
                Clear
              </Button>
            </div>
          </div>
          
          <TextField
            multiline
            rows={12}
            fullWidth
            value={inputJson}
            onChange={handleInputChange}
            placeholder="Paste your JSON payload here..."
            variant="outlined"
            error={!isValidJson}
            helperText={!isValidJson ? 'Invalid JSON format' : `${inputJson.length} characters`}
            className="json-input"
            InputProps={{
              style: { fontFamily: 'Monaco, Consolas, "Courier New", monospace' }
            }}
          />
        </div>

        <div className="transform-section">
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isTransforming ? <CircularProgress size={20} /> : <Transform />}
            onClick={handleTransform}
            disabled={!inputJson.trim() || !isValidJson || isTransforming}
            className="transform-button"
          >
            {isTransforming ? 'Transforming...' : 'Transform'}
          </Button>
        </div>

        <div className="output-section">
          <div className="section-header">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Transformed Onex RCS-API Payload</Typography>
            {outputJson && (
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyOutput}
                variant="outlined"
              >
                Copy
              </Button>
            )}
          </div>
          
          <TextField
            multiline
            rows={12}
            fullWidth
            value={outputJson}
            placeholder="Transformed JSON will appear here..."
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { fontFamily: 'Monaco, Consolas, "Courier New", monospace' }
            }}
            className="json-output"
          />
        </div>
      </div>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}
    </div>
  );
};
